import { useDispatch, useSelector } from "react-redux";
import {
  publishPlan,
} from "../../redux/features/plannings/planning-slice";
import { IPlanDetail } from "../../types/planning";
import { Box, Button, Dialog, Typography, Grid } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { Close } from "@mui/icons-material";
import { RootState } from "src/redux/store";
import { BookedSchedule } from "src/types/schedule";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface MonthlyScheduleSummary {
  month: string;
  count: number;
  isSelected?: boolean;
}

const Publish: React.FC<{ currentPlan: IPlanDetail,pendingSchedules: BookedSchedule[] | null }> = ({ currentPlan,pendingSchedules }) => {
  const [openPopup, setOpenPopUp] = useState(false);
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs()); // today
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [dateFilteredSchedules, setDateFilteredSchedules] = useState<BookedSchedule[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<MonthlyScheduleSummary[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);

  const groupSchedulesByMonth = (schedules: BookedSchedule[]): MonthlyScheduleSummary[] => {
    const groupedSchedules = schedules.reduce((acc: { [key: string]: number }, schedule) => {
      const month = dayjs(schedule.specificDate).format('MMMM YYYY');
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(groupedSchedules)
      .map(([month, count]) => ({
        month,
        count,
        isSelected: month === selectedMonth
      }))
      .sort((a, b) => dayjs(a.month, 'MMMM YYYY').diff(dayjs(b.month, 'MMMM YYYY')));
  };

  // Group all pending schedules by month
  const allMonthsSummary = useMemo(() => {
    return groupSchedulesByMonth(pendingSchedules ?? []);
  }, [pendingSchedules, selectedMonth]);

  const handleMonthSelect = (month: string) => {
    if (selectedMonth === month) {
      setSelectedMonth(null);
      setIsCustomDateRange(false);
      setStartDate(dayjs());
      setEndDate(dayjs().add(2, "day"));
    } else {
      setSelectedMonth(month);
      setIsCustomDateRange(false);
      const monthDate = dayjs(month, 'MMMM YYYY');
      setStartDate(monthDate.startOf('month'));
      setEndDate(monthDate.endOf('month'));
    }
  };

  const handleCustomDateChange = (start: Dayjs | null, end: Dayjs | null) => {
    setIsCustomDateRange(true);
    setSelectedMonth(null);
    setStartDate(start);
    setEndDate(end);
  };

  // Apply date filter to pending schedules
  const handleDateFilter = () => {
    if (!startDate || !endDate || startDate >= endDate) {
      setDateFilteredSchedules([]);
      setMonthlySummary([]);
      return;
    }

    const start = startDate.startOf("day");
    const end = endDate.endOf("day");

    const filteredSchedules = pendingSchedules?.filter(schedule => {
      const date = dayjs(schedule.specificDate);
      return date.isSameOrAfter(start) && date.isSameOrBefore(end);
    });
    if(!filteredSchedules) return;
    setDateFilteredSchedules(filteredSchedules);
    if (!isCustomDateRange) {
      setMonthlySummary([]); // Don't show monthly breakdown when a month is selected
    } else {
      setMonthlySummary(groupSchedulesByMonth(filteredSchedules));
    }
  };

  const handlePublish = () => {
    if (dateFilteredSchedules.length === 0) return setOpenPopUp(false);
    const scheduleIds = [...new Set(dateFilteredSchedules.map((item) => item.id))];
    dispatch(publishPlan({ Id: currentPlan.plan.id, scheduleIds }));
    setOpenPopUp(false);
  };

  useEffect(() => {
    handleDateFilter();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, pendingSchedules]);
console.log(pendingSchedules,"pendingSchedules");

  return (
    <div className="publish-plan">
      <div className="statusBtnPart">
        <button className="btn publishBtn" onClick={() => setOpenPopUp(true)}>
          Publish ({pendingSchedules ? pendingSchedules.length : currentPlan.pendingCount})
        </button>
      </div>
      <Dialog open={openPopup} className="smallModel publishModal">
        <Box className="smallModel__head">
          <Typography variant="h5" className="smallModel__title">
            Publish Plan
          </Typography>
          <Button
            className="smallModel__closeBtn"
            onClick={() => {
              setOpenPopUp(false);
              setStartDate(dayjs());
              setEndDate(dayjs().add(2, "day"));
              setSelectedMonth(null);
              setIsCustomDateRange(false);
            }}
          >
            <Close />
          </Button>
        </Box>

        <Box className="publish-modal-body">
          {pendingSchedules && pendingSchedules?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Select a Month:</Typography>
              <Grid container spacing={2}>
                {allMonthsSummary.map((summary) => (
                  <Grid item xs={6} sm={4} key={summary.month}>
                    <Box
                      onClick={() => handleMonthSelect(summary.month)}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: summary.isSelected ? '#e3f2fd' : '#f5f5f5',
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: summary.isSelected ? '#e3f2fd' : '#eeeeee',
                        }
                      }}
                    >
                      <Typography variant="subtitle2" color="textSecondary">
                        {summary.month}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {summary.count}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Box className="publish-dateRange">
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Select Custom Range:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className="singleDatePicker DatePickerFilter"
                label="Start Date"
                value={startDate}
                onChange={(e) => handleCustomDateChange(e, endDate)}
              />

              <DatePicker
                className="singleDatePicker DatePickerFilter"
                label="End Date"
                value={endDate}
                onChange={(e) => handleCustomDateChange(startDate, e)}
              />
            </LocalizationProvider>
          </Box>
          
          {isCustomDateRange && monthlySummary.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Selected Range Summary:</Typography>
              <Grid container spacing={2}>
                {monthlySummary.map((summary) => (
                  <Grid item xs={6} sm={4} key={summary.month}>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#f5f5f5',
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="subtitle2" color="textSecondary">
                        {summary.month}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {summary.count}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {dateFilteredSchedules.length === 0 && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No pending schedules found for this date range.
            </Typography>
          )}

          {dateFilteredSchedules.length > 0 && (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
              {dateFilteredSchedules.length} schedules selected for publishing
            </Typography>
          )}
        </Box>
        <Box className="publish-footer">
          <Button
            disabled={dateFilteredSchedules.length === 0}
            className="button"
            variant="contained"
            onClick={handlePublish}
          >
            {dateFilteredSchedules.length > 0 ? "Publish Selected" : "Cancel"}
          </Button>
        </Box>
      </Dialog>
    </div>
  );
};

export default Publish;
